import { karachiBusinessDate } from '@/lib/dates'
import { sumPaisa } from '@/lib/money'
import { saleOutstanding } from '@/features/payments/outstanding'
import type { Contact, Device, Payment, Purchase, Sale } from '@/offline/types'

export type TxnType = 'purchase' | 'sale' | 'payment'

export interface TxnRow {
  id: string
  type: TxnType
  at: string
  dateKey: string // Asia/Karachi business date (YYYY-MM-DD)
  label: string
  detail: string
  amountPaisa: number
  direction: 'in' | 'out'
}

export interface TxnInputs {
  purchases: Purchase[]
  sales: Sale[]
  payments: Payment[]
  devices: Device[]
  contacts: Contact[]
}

/**
 * Chronological day-book. Purchases/sales carry their accrual value; payments carry
 * cash movement. Kept separate so value and cash are never conflated (bookkeeping rule).
 */
export function buildTransactions(inp: TxnInputs): TxnRow[] {
  const deviceModel = (id: string) => inp.devices.find((d) => d.id === id)?.model ?? 'Device'
  const contactName = (id: string | null) =>
    id ? (inp.contacts.find((c) => c.id === id)?.name ?? 'Customer') : 'Walk-in'

  const rows: TxnRow[] = []

  for (const p of inp.purchases.filter((x) => !x.deleted_at)) {
    rows.push({
      id: p.id,
      type: 'purchase',
      at: p.purchased_at,
      dateKey: '',
      label: deviceModel(p.device_id),
      detail: 'stock purchase',
      amountPaisa: p.cost_paisa + p.direct_cost_paisa,
      direction: 'out',
    })
  }
  for (const s of inp.sales.filter((x) => !x.deleted_at)) {
    rows.push({
      id: s.id,
      type: 'sale',
      at: s.sold_at,
      dateKey: '',
      label: deviceModel(s.device_id),
      detail: contactName(s.customer_contact_id),
      amountPaisa: s.sale_amount_paisa,
      direction: 'in',
    })
  }
  for (const pay of inp.payments.filter((x) => !x.deleted_at)) {
    rows.push({
      id: pay.id,
      type: 'payment',
      at: pay.paid_at,
      dateKey: '',
      label: pay.direction === 'in' ? 'Cash received' : 'Cash paid',
      detail: pay.related_type,
      amountPaisa: pay.amount_paisa,
      direction: pay.direction,
    })
  }

  for (const r of rows) r.dateKey = karachiBusinessDate(r.at)
  return rows.sort((a, b) => b.at.localeCompare(a.at))
}

export interface RangeTotals {
  salesValue: number
  purchaseValue: number
  cashIn: number
  cashOut: number
  count: number
}

/** Day-book totals for a Karachi-date range. cashIn = sale received-at-sale + later receipts. */
export function rangeTotals(inp: TxnInputs, rows: TxnRow[], fromKey?: string, toKey?: string): RangeTotals {
  const inRange = rows.filter((r) => (!fromKey || r.dateKey >= fromKey) && (!toKey || r.dateKey <= toKey))
  const sales = inp.sales.filter((s) => !s.deleted_at && inRange.some((r) => r.type === 'sale' && r.id === s.id))
  const payments = inp.payments.filter((p) => !p.deleted_at && inRange.some((r) => r.type === 'payment' && r.id === p.id))

  const salesValue = sumPaisa(sales.map((s) => s.sale_amount_paisa))
  const purchaseValue = sumPaisa(
    inRange.filter((r) => r.type === 'purchase').map((r) => r.amountPaisa),
  )
  const cashIn = sumPaisa([
    ...sales.map((s) => s.received_paisa),
    ...payments.filter((p) => p.direction === 'in').map((p) => p.amount_paisa),
  ])
  const cashOut = sumPaisa(payments.filter((p) => p.direction === 'out').map((p) => p.amount_paisa))

  return { salesValue, purchaseValue, cashIn, cashOut, count: inRange.length }
}

export interface AgingBuckets {
  d0_30: number
  d31_60: number
  d60p: number
}

export interface ContactBalance {
  contact: Contact
  outstanding: number
  aging: AgingBuckets
  oldestUnpaidAt: string | null
}

/**
 * Accounts-receivable subledger: per-contact outstanding with aging buckets
 * (0-30 / 31-60 / 60+ days since sale). Balances are derived, never stored.
 */
export function contactBalances(
  sales: Sale[],
  payments: Payment[],
  contacts: Contact[],
  nowMs: number = Date.now(),
): ContactBalance[] {
  const out: ContactBalance[] = []
  for (const contact of contacts.filter((c) => !c.deleted_at)) {
    const linked = sales.filter((s) => !s.deleted_at && !s.returned_at && s.customer_contact_id === contact.id)
    let outstanding = 0
    const aging: AgingBuckets = { d0_30: 0, d31_60: 0, d60p: 0 }
    let oldest: string | null = null
    for (const s of linked) {
      const bal = saleOutstanding(s, payments)
      if (bal <= 0) continue
      outstanding += bal
      const ageDays = Math.floor((nowMs - new Date(s.sold_at).getTime()) / 86400000)
      if (ageDays <= 30) aging.d0_30 += bal
      else if (ageDays <= 60) aging.d31_60 += bal
      else aging.d60p += bal
      if (oldest === null || s.sold_at < oldest) oldest = s.sold_at
    }
    out.push({ contact, outstanding, aging, oldestUnpaidAt: oldest })
  }
  return out.sort((a, b) => b.outstanding - a.outstanding)
}
